/**
 * Commercial quotes. The API's OpenAPI now carries the quote schemas, so the shapes
 * come straight from the generated contract — this module only aliases them (matching
 * `contract.ts`'s single-re-export convention) and holds the request functions.
 *
 * Two asymmetries the generated types encode, and the UI depends on:
 *   • `CustomerQuote` has `recipientCount` instead of `recipients`, and no `notes` at
 *     all — the recipient list and the operator's internal notes never leave the
 *     support console.
 *   • `CustomerQuoteInvoice.dueAt` and `.billingInterval` are nullable, because the
 *     Stripe invoice is created asynchronously; every render path handles that.
 */
import { request } from "./client";
import type { components } from "./contract";
import { AppError } from "./problem";

type Schemas = components["schemas"];

export type QuoteStatus = Schemas["AdminQuoteStatus"];
export type QuoteOption = Schemas["AdminQuoteOption"];
export type QuoteDelivery = Schemas["AdminQuoteDelivery"];
/** Per-recipient outcome of the portal-link email fan-out. */
export type QuoteDeliveryStatus = QuoteDelivery["status"];

/** Support-side view. Carries the full recipient list — admin console only. */
export type AdminQuote = Schemas["AdminQuote"];
export type AdminQuoteWithActivity = Schemas["AdminQuoteWithActivity"];
/**
 * Customer-side view. The recipient list is deliberately absent: a procurement
 * contact must never be able to enumerate who else received the quote.
 */
export type CustomerQuote = Schemas["CustomerQuote"];

export type AdminQuoteOptionInput = Schemas["AdminQuoteOptionCreateRequest"];
export type AdminQuoteCreateRequest = Schemas["AdminQuoteCreateRequest"];
export type AdminQuoteCreation = Schemas["AdminQuoteCreation"];

/** The Stripe invoice materialized by accepting exactly one option. */
export type QuoteInvoice = Schemas["CustomerQuoteInvoice"];
export type QuoteAcceptance = Schemas["CustomerQuoteAcceptance"];
export type QuoteAcceptRequest = Schemas["CustomerQuoteAcceptanceRequest"];

/** `new` offers options for a new subscription; `modify` changes an existing one. */
export type QuoteKind = Schemas["AdminQuoteKind"];
/** Present only on `kind: "modify"` quotes (plan §3.3). */
export type QuoteModification = Schemas["QuoteModification"];
export type QuoteModificationBase = Schemas["QuoteModificationBase"];
/** Amount due now, computed at read time; null once the change has been applied. */
export type QuoteProration = Schemas["QuoteProration"];
export type QuoteModificationResult = Schemas["QuoteModificationResult"];
/** `409 quote_amount_changed` body: `amountDue` is the live prorated amount to confirm. */
export type QuoteAmountChangedProblem = Schemas["QuoteAmountChangedProblem"];

/**
 * The live amount from a `409 quote_amount_changed` acceptance failure, or null for any
 * other error. Confirm again with `expectedAmountDue: amountDue` and a fresh Idempotency-Key.
 */
export function quoteAmountChanged(error: unknown): { amountDue: number } | null {
  if (!(error instanceof AppError) || error.code !== "quote_amount_changed") return null;
  const amountDue = error.extensions?.["amountDue"];
  return typeof amountDue === "number" && Number.isSafeInteger(amountDue) && amountDue >= 0 ? { amountDue } : null;
}

// ------------------------------------------------------------------ support side

export async function listAdminQuotes(customerId: string, signal?: AbortSignal): Promise<AdminQuoteWithActivity[]> {
  const res = await request<AdminQuoteWithActivity[]>(
    `/v1/admin/entitlement-customers/${encodeURIComponent(customerId)}/quotes`,
    signal ? { signal } : {}
  );
  return res.data;
}

export async function createAdminQuote(
  customerId: string,
  body: AdminQuoteCreateRequest,
  idempotencyKey: string
): Promise<AdminQuoteCreation> {
  const res = await request<AdminQuoteCreation>(
    `/v1/admin/entitlement-customers/${encodeURIComponent(customerId)}/quotes`,
    { method: "POST", body, idempotencyKey }
  );
  return res.data;
}

export async function resendAdminQuote(
  customerId: string,
  quoteId: string,
  idempotencyKey: string
): Promise<AdminQuoteCreation> {
  const res = await request<AdminQuoteCreation>(
    `/v1/admin/entitlement-customers/${encodeURIComponent(customerId)}/quotes/${encodeURIComponent(quoteId)}/resend`,
    { method: "POST", idempotencyKey }
  );
  return res.data;
}

// ----------------------------------------------------------------- customer side

/**
 * Authorization is server-side and recipient-based: a signed-in organization member
 * only sees the quotes their own verified address was named on. The route guard in
 * front of these screens is convenience, never the security boundary.
 */
export async function listOrganizationQuotes(orgId: string, signal?: AbortSignal): Promise<CustomerQuote[]> {
  const res = await request<CustomerQuote[]>(
    `/v1/orgs/${encodeURIComponent(orgId)}/quotes`,
    signal ? { signal } : {}
  );
  return res.data;
}

export async function getOrganizationQuote(
  orgId: string,
  quoteId: string,
  signal?: AbortSignal
): Promise<CustomerQuote> {
  const res = await request<CustomerQuote>(
    `/v1/orgs/${encodeURIComponent(orgId)}/quotes/${encodeURIComponent(quoteId)}`,
    signal ? { signal } : {}
  );
  return res.data;
}

/**
 * For a modify quote pass `expectedAmountDue` (the `modification.proration.amountDue`
 * shown); a different live amount rejects with `quote_amount_changed` — see
 * `quoteAmountChanged()`. `paymentUrl` is then the prorated invoice, or null when
 * nothing is due now.
 */
export async function acceptOrganizationQuote(
  orgId: string,
  quoteId: string,
  body: QuoteAcceptRequest,
  idempotencyKey: string
): Promise<QuoteAcceptance> {
  const res = await request<QuoteAcceptance>(
    `/v1/orgs/${encodeURIComponent(orgId)}/quotes/${encodeURIComponent(quoteId)}/accept`,
    { method: "POST", body, idempotencyKey }
  );
  return res.data;
}
